import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from './useAuth';
import { useClinic } from '@/stores/ClinicContext';
import { db } from '@/services/firebase/config';
import { Role } from '@/types/auth';
import type { Permissions } from '@/types/auth';
import type { Clinic } from '@/types/models';
import {
  hasEffectivePermission,
  type PermissionAction,
} from '@/config/permissionCatalog';

// ── Plan limit definitions (FR-134) ──────────────────────────────────────────

type PlanKey = 'FREE' | 'PRO' | 'ENTERPRISE';

interface PlanLimits {
  maxDoctors: number;
  maxUnits: number;
  maxPatients: number;
  monthlyTokens: number;
  storageGB: number;
  telemedicine: boolean;
  portal: boolean;
  marketing: boolean;
  advancedReports: boolean;
}

const PLAN_LIMITS: Record<PlanKey, PlanLimits> = {
  FREE: {
    maxDoctors: 1,
    maxUnits: 1,
    maxPatients: 200,
    monthlyTokens: 50,
    storageGB: 1,
    telemedicine: false,
    portal: false,
    marketing: false,
    advancedReports: false,
  },
  PRO: {
    maxDoctors: 5,
    maxUnits: 3,
    maxPatients: 2000,
    monthlyTokens: 500,
    storageGB: 10,
    telemedicine: true,
    portal: true,
    marketing: true,
    advancedReports: true,
  },
  ENTERPRISE: {
    maxDoctors: Infinity,
    maxUnits: Infinity,
    maxPatients: Infinity,
    monthlyTokens: 5000,
    storageGB: 100,
    telemedicine: true,
    portal: true,
    marketing: true,
    advancedReports: true,
  },
};

// ── Resource → limit key mapping ─────────────────────────────────────────────

type Resource = 'doctors' | 'units' | 'patients' | 'tokens' | 'storage';
type Feature = 'telemedicine' | 'portal' | 'marketing' | 'advancedReports';

const RESOURCE_LIMIT_KEY: Record<Resource, keyof PlanLimits> = {
  doctors: 'maxDoctors',
  units: 'maxUnits',
  patients: 'maxPatients',
  tokens: 'monthlyTokens',
  storage: 'storageGB',
};

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useRBAC() {
  const { profile, loading } = useAuth();
  const { clinicId, isOwner } = useClinic();
  const [clinic, setClinic] = useState<Clinic | null>(null);
  /**
   * Permissões finas LIVE do próprio profissional, lidas direto do doc
   * `professionals/{uid}` — não do token. Assim, mudanças feitas na tela
   * Equipe valem na hora (o token só recarrega no logout/login). `undefined`
   * = ainda não carregou; ausência do campo = usa o padrão do cargo.
   */
  const [livePermissions, setLivePermissions] = useState<Permissions | undefined>(
    undefined,
  );

  // Subscribe to the clinic document so plan changes are reflected in real time.
  useEffect(() => {
    if (!clinicId) {
      setClinic(null);
      return;
    }

    const unsub = onSnapshot(
      doc(db, 'clinics', clinicId),
      (snap) => {
        if (snap.exists()) {
          setClinic({ id: snap.id, ...snap.data() } as Clinic);
        } else {
          setClinic(null);
        }
      },
      () => {
        // On permission error or network failure, leave existing state intact.
      }
    );

    return unsub;
  }, [clinicId]);

  // Subscribe to the current user's professional doc for live permissions.
  useEffect(() => {
    if (!clinicId || !profile?.uid) {
      setLivePermissions(undefined);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'clinics', clinicId, 'professionals', profile.uid),
      (snap) => {
        const data = snap.data() as { permissions?: Permissions } | undefined;
        setLivePermissions(data?.permissions);
      },
      () => {
        // Keep whatever we had on error — hasPermission falls back to role default.
      },
    );
    return unsub;
  }, [clinicId, profile?.uid]);

  // ── Existing functions ──────────────────────────────────────────────────────

  const hasRole = (allowedRoles: Role[]) => {
    if (loading) return false;
    if (!profile) return false;
    return allowedRoles.includes(profile.role);
  };

  /**
   * Permissão efetiva do usuário atual para um módulo × ação.
   *
   * Regras:
   *  - Admin e o dono da clínica sempre têm acesso (nunca se trancam fora).
   *  - Caso contrário: usa o mapa `permissions` salvo quando existe (concede
   *    além / revoga aquém do cargo); sem mapa salvo, cai no padrão do cargo.
   *
   * Lê as permissões AO VIVO do doc do profissional; enquanto isso não chega,
   * usa as do token como ponte.
   */
  const hasPermission = (module: string, action: PermissionAction) => {
    if (loading) return false;
    if (!profile) return false;
    if (profile.role === Role.ADMIN || isOwner) return true;

    const stored = livePermissions ?? profile.permissions;
    return hasEffectivePermission(profile.role, stored, module, action);
  };

  // ── Plan limit enforcement (T062) ───────────────────────────────────────────

  /**
   * Check whether adding one more item of `resource` is within the clinic's
   * plan limits.
   *
   * @param resource - The resource type to check.
   * @param currentCount - The current number of that resource already in use.
   * @returns `{ allowed, limit, current }` where `allowed` is true when
   *          `currentCount` is strictly less than the plan limit.
   */
  const checkPlanLimit = (
    resource: Resource,
    currentCount: number
  ): { allowed: boolean; limit: number; current: number } => {
    const plan: PlanKey = (clinic?.plan as PlanKey) ?? 'FREE';
    const limits = PLAN_LIMITS[plan];
    const limitKey = RESOURCE_LIMIT_KEY[resource];
    const limit = limits[limitKey] as number;

    return {
      allowed: currentCount < limit,
      limit,
      current: currentCount,
    };
  };

  /**
   * Returns whether the clinic's current plan includes the given feature.
   */
  const hasFeature = (feature: Feature): boolean => {
    const plan: PlanKey = (clinic?.plan as PlanKey) ?? 'FREE';
    return PLAN_LIMITS[plan][feature] as boolean;
  };

  return { hasRole, hasPermission, checkPlanLimit, hasFeature };
}
